import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Loader2, Save, Plus, Trash2, Building2, Info, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MAX_NAME = 200;
const MAX_ADDRESS = 500;
const MAX_CODE = 20;
const MAX_ID = 30;

function blankEntity(i) {
  return { id: `custom_${i}`, code: '', name: '', address: '', is_default: false };
}

export default function SeertechEntitiesTab() {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/settings/seertech-entities`,
        { withCredentials: true });
      const loaded = res.data?.entities || [];
      setEntities(loaded.length ? loaded : [blankEntity(0)]);
      setMeta({ updated_at: res.data?.updated_at, updated_by: res.data?.updated_by });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Seertech entities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntities(); }, [fetchEntities]);

  const updateField = (idx, field, value) => {
    setEntities(list => list.map((e, i) => {
      if (i !== idx) {
        // Flipping one to default unflips the rest.
        if (field === 'is_default' && value) return { ...e, is_default: false };
        return e;
      }
      let v = value;
      if (field === 'name') v = v.slice(0, MAX_NAME);
      else if (field === 'address') v = v.slice(0, MAX_ADDRESS);
      else if (field === 'code') v = v.slice(0, MAX_CODE);
      else if (field === 'id') v = String(v).toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, MAX_ID);
      return { ...e, [field]: v };
    }));
  };

  const addEntity = () => {
    setEntities(list => [...list, blankEntity(list.length)]);
  };

  // Comma-list fields keep raw text while typing (so spaces/commas aren't
  // swallowed) and parse to arrays on blur/save.
  const parseList = (raw) => String(raw || '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
  const listValue = (entity, textField, arr) =>
    entity[textField] != null ? entity[textField] : (arr || []).join(', ');
  const blurList = (idx, textField, arrayField) => {
    setEntities(list => list.map((e, i) => {
      if (i !== idx || e[textField] == null) return e;
      const parsed = parseList(e[textField]);
      return { ...e, [arrayField]: parsed, [textField]: parsed.join(', ') };
    }));
  };

  const removeEntity = (idx) => {
    setEntities(list => {
      if (list.length === 1) return [blankEntity(0)];
      const next = list.filter((_, i) => i !== idx);
      if (!next.some(e => e.is_default) && next.length) next[0].is_default = true;
      return next;
    });
  };

  const save = async () => {
    const cleaned = entities
      .map(e => {
        const currencies = e._curr_text != null ? parseList(e._curr_text) : (e.available_currencies || []);
        const regions = e._regions_text != null ? parseList(e._regions_text)
          : (e.regions && e.regions.length ? e.regions : (e.region ? [e.region] : []));
        return {
          id: (e.id || '').trim(),
          code: (e.code || '').trim(),
          name: (e.name || '').trim(),
          address: (e.address || '').trim(),
          default_currency_code: (e.default_currency_code || '').trim().toUpperCase() || null,
          available_currencies: currencies,
          regions,
          region: (regions[0] || '').trim().toUpperCase() || null,
          fy_start_month: Number(e.fy_start_month || 7),
          fy_label_style: e.fy_label_style === 'start' ? 'start' : 'end',
          is_default: !!e.is_default,
        };
      })
      .filter(e => e.id && e.name);

    if (!cleaned.length) {
      toast.error('Add at least one entity with an id and name');
      return;
    }
    if (!cleaned.some(e => e.is_default)) cleaned[0].is_default = true;

    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/settings/seertech-entities`,
        { entities: cleaned }, { withCredentials: true });
      toast.success(res.data?.message || 'Seertech entities saved');
      fetchEntities();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save entities');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-10">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading Seertech entities…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="seertech-entities-tab">
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-[#0F2040] text-base">
            <Building2 className="w-4 h-4 text-[#14B8A6]" />
            Seertech Contracting Entities
          </CardTitle>
          <div className="text-[11px] text-slate-500 flex items-start gap-1.5 mt-1">
            <Info className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
            The legal Seertech entity selected on each SOW flows into the
            Executive Summary preamble and the signature block. Edit the
            registered addresses once — every SOW generated afterwards
            picks them up automatically.
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {entities.map((entity, idx) => (
            <div key={idx} className="border rounded-lg p-3 bg-white space-y-2"
                 data-testid={`entity-row-${idx}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-400 w-5 text-center">
                    {idx + 1}.
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant={entity.is_default ? 'default' : 'outline'}
                    onClick={() => updateField(idx, 'is_default', !entity.is_default)}
                    className={`h-7 text-[10px] ${entity.is_default ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 border-amber-300' : ''}`}
                    data-testid={`entity-default-toggle-${idx}`}
                  >
                    <Star className={`w-3 h-3 mr-1 ${entity.is_default ? 'fill-amber-500 text-amber-500' : ''}`} />
                    {entity.is_default ? 'Default' : 'Set default'}
                  </Button>
                </div>
                <Button
                  type="button" size="sm" variant="ghost"
                  onClick={() => removeEntity(idx)}
                  className="h-7 text-slate-400 hover:text-rose-600"
                  data-testid={`entity-remove-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                    ID *
                  </label>
                  <Input
                    value={entity.id}
                    onChange={e => updateField(idx, 'id', e.target.value)}
                    placeholder="us, au, emea..."
                    className="h-8 text-xs font-mono"
                    data-testid={`entity-id-${idx}`}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                    Region Code
                  </label>
                  <Input
                    value={entity.code}
                    onChange={e => updateField(idx, 'code', e.target.value)}
                    placeholder="US / AU / EMEA"
                    className="h-8 text-xs"
                    data-testid={`entity-code-${idx}`}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                    Legal Name *
                  </label>
                  <Input
                    value={entity.name}
                    onChange={e => updateField(idx, 'name', e.target.value)}
                    placeholder="Seertech Solutions Pty Ltd"
                    className="h-8 text-xs"
                    data-testid={`entity-name-${idx}`}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                    Default Currency
                  </label>
                  <Input
                    value={entity.default_currency_code || ''}
                    onChange={e => updateField(idx, 'default_currency_code', e.target.value.toUpperCase())}
                    placeholder="EUR / AUD / USD"
                    className="h-8 text-xs font-mono"
                    data-testid={`entity-default-currency-${idx}`}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                    Available Currencies (comma-separated)
                  </label>
                  <Input
                    value={listValue(entity, '_curr_text', entity.available_currencies)}
                    onChange={e => updateField(idx, '_curr_text', e.target.value)}
                    onBlur={() => blurList(idx, '_curr_text', 'available_currencies')}
                    placeholder="EUR, GBP, USD"
                    className="h-8 text-xs font-mono"
                    data-testid={`entity-available-currencies-${idx}`}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                    Regions (comma-separated)
                  </label>
                  <Input
                    value={listValue(entity, '_regions_text',
                      entity.regions && entity.regions.length ? entity.regions : (entity.region ? [entity.region] : []))}
                    onChange={e => updateField(idx, '_regions_text', e.target.value)}
                    onBlur={() => blurList(idx, '_regions_text', 'regions')}
                    placeholder="APAC, ANZ, SEA"
                    className="h-8 text-xs font-mono"
                    data-testid={`entity-regions-${idx}`}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Financial year starts in</label>
                  <select value={entity.fy_start_month || 7} onChange={e => updateField(idx, 'fy_start_month', Number(e.target.value))} className="h-8 w-full text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid={`entity-fy-start-${idx}`}>
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">FY label</label>
                  <select value={entity.fy_label_style || 'end'} onChange={e => updateField(idx, 'fy_label_style', e.target.value)} className="h-8 w-full text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid={`entity-fy-label-${idx}`}>
                    <option value="end">Year the FY ends (Jul 2025–Jun 2026 = FY26)</option>
                    <option value="start">Year the FY starts (Jul 2025–Jun 2026 = FY25)</option>
                  </select>
                </div>
                <p className="text-[10px] text-slate-400 self-end pb-1" data-testid={`entity-fy-hint-${idx}`}>Deals are assigned to a FY from their close date using this entity's FY.</p>
              </div>
              <div>
                <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                  Registered Address
                </label>
                <Textarea
                  value={entity.address}
                  onChange={e => updateField(idx, 'address', e.target.value)}
                  maxLength={MAX_ADDRESS}
                  rows={2}
                  placeholder="Street, City, State, Postal Code, Country"
                  className="text-xs"
                  data-testid={`entity-address-${idx}`}
                />
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between pt-1">
            <Button type="button" size="sm" variant="outline"
              onClick={addEntity}
              className="h-7 text-[11px]"
              data-testid="entity-add-btn">
              <Plus className="w-3 h-3 mr-1" />
              Add entity
            </Button>
            <div className="text-[10px] text-slate-400">
              {entities.length} entit{entities.length === 1 ? 'y' : 'ies'}
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-slate-200">
            <div className="text-[10px] text-slate-400">
              {meta.updated_at
                ? <>Last saved {new Date(meta.updated_at).toLocaleString()} by {meta.updated_by || 'admin'}</>
                : <>Using seeded defaults (US / AU / EMEA) — save to persist edits</>}
            </div>
            <Button type="button" size="sm" onClick={save} disabled={saving}
              className="bg-[#0F2040] hover:bg-[#1A3055] text-white h-7 text-[11px]"
              data-testid="entity-save-btn">
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              Save entities
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
